import { Box, Grid, Typography, dialogActionsClasses, Stack } from "@mui/material";
import MzInput from "@markaaz/ui/MzInputControls";
import MzAlert from "@markaaz/ui/MzAlert";
import MzButton from "@markaaz/ui/MzButton";
import { useFormik } from "formik";
import { MzDialog, MzDialogContent, MzDialogActions } from "@markaaz/ui/MzDialogControls";
import * as yup from "yup";
import I18n from "i18next";

const sx = {
  root: {
    display: "flex",
    flexDirection: "column",
    gap: "40px"
  },
  wrap: {
    display: "flex",
    padding: "40px",
    gap: "40px",
    backgroundColor: "common.white",
    height: "231px",
    width: "660px",
    borderRadius: "5px"
  },
  button: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    padding: "24px 5px",
    gap: "20px"
  },
  disabled: {
    "& input[disabled]": {
      backgroundColor: (theme) => theme.palette.darkBlue.c5
    }
  },
  confirmText: {
    color: "darkBlue.c100",
    width: "500px",
    textAlign: "center"
  },
  content: {
    [`.${dialogActionsClasses.root}`]: {
      display: "flex",
      justifyContent: "center"
    }
  },
  subHeading: {
    color: (theme) => theme.palette.darkBlue.c100
  },
  dialogActions: {
    justifyContent: "center"
  }
};

const validationSchema = yup.object({
  userEmail: yup
    .string()
    .email(I18n.t("authPages.validEmailAddress"))
    .required("Email is required")
    .matches(/[a-z0-9]+@[a-z]+\.[a-z]{2,3}/, I18n.t("authPages.validEmailAddress")),

  oldPassword: yup
    .string()
    .required("Old Password is required")
    .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])(?=.{8,})/, I18n.t("authPages.passwordCriteria")),

  createPassword: yup
    .string()
    .required("Password is required")
    .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])(?=.{8,})/, I18n.t("authPages.passwordCriteria")),

  confirmPassword: yup
    .string()
    .required("Please re-enter your password")
    .oneOf([yup.ref("createPassword"), null], "Passwords must match")
});

export default function AccountSecurity({
  userData,
  loading,
  onSubmit,
  dialogOpen,
  countDownLogout,
  onClose,
  onAlertClose,
  showNotification
}) {
  const initialValues = {
    userEmail: userData?.email || "",
    oldPassword: "",
    createPassword: "",
    confirmPassword: ""
  };

  const formik = useFormik({
    initialValues: initialValues,
    onSubmit: async () => {
      await onSubmit(formik.values);
    },
    validationSchema,
    enableReinitialize: true,
    validateOnMount: true
  });

  return (
    <Box sx={sx.root}>
      <Box>
        <Typography sx={sx.subHeading} variant="h6">
          Manage Password
        </Typography>
      </Box>
      <Box sx={sx.wrap}>
        <Grid container spacing={5}>
          <Grid item xs={6}>
            <MzInput
              sx={sx.disabled}
              name="userEmail"
              label="User Email"
              autoComplete="off"
              type="email"
              disabled={true}
              value={formik.values.userEmail}
              fullWidth
              error={formik.touched.userEmail && !!formik.errors.userEmail}
              helperText={formik.touched.userEmail && formik.errors.userEmail}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </Grid>

          <Grid item xs={6}>
            <MzInput
              sx={sx.input}
              name="oldPassword"
              label="Old Password"
              autoComplete="new-password"
              type="password"
              required
              fullWidth
              error={formik.touched.oldPassword && !!formik.errors.oldPassword}
              helperText={formik.touched.oldPassword && formik.errors.oldPassword}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </Grid>
          <Grid item xs={6}>
            <MzInput
              sx={sx.input}
              name="createPassword"
              label="Create New Password"
              type="password"
              autoComplete="new-password"
              required
              fullWidth
              error={formik.touched.createPassword && !!formik.errors.createPassword}
              helperText={
                (formik.touched.createPassword && formik.errors.createPassword) || I18n.t("authPages.passwordCriteria")
              }
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </Grid>
          <Grid item xs={6}>
            <MzInput
              sx={sx.input}
              name="confirmPassword"
              label="Re-enter New Password"
              type="password"
              autoComplete="confirmPassword"
              required
              fullWidth
              error={formik.touched.confirmPassword && !!formik.errors.confirmPassword}
              helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </Grid>
        </Grid>
      </Box>
      <Stack direction="row" gap={10} alignItems="center">
        <MzButton
          variant="contained"
          color="primary"
          size="large"
          onClick={formik.handleSubmit}
          loading={loading}
          disabled={!formik.isValid || formik.isSubmitting}
        >
          Update
        </MzButton>
        {showNotification.message && (
          <MzAlert
            sx={{ alignItems: "center" }}
            severity={showNotification.type === "success" ? "success" : "error"}
            onClose={() => {
              onAlertClose("aci");
            }}
          >
            {showNotification.message}
          </MzAlert>
        )}
      </Stack>

      <MzDialog open={dialogOpen}>
        <MzDialogContent>
          <Typography variant="body1" sx={sx.confirmText}>
            Password has been successfully changed. You will be automatically logged out in {countDownLogout} seconds.
          </Typography>
        </MzDialogContent>
        <MzDialogActions sx={sx.dialogActions}>
          <MzButton variant="outlined" color="secondary" size="small" onClick={onClose} sx={sx.confirmButton}>
            OK
          </MzButton>
        </MzDialogActions>
      </MzDialog>
    </Box>
  );
}
